import React, { useState, useEffect } from 'react';
import { Box, IconButton, TextField, CircularProgress, Autocomplete, InputLabel, Stack, Tooltip } from '@mui/material';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import { CloseOutlined } from '@ant-design/icons';
import RoleModal from './RoleModal';
import { useGetPermissions } from 'api/authUser';
import { useNavigate } from 'react-router-dom';

// types
import { FormikContext } from 'formik';
import { Role } from 'types/userrole';
import { useGetRoleLookupList } from 'api/userrole';
import Lookup from 'components/controls/Lookup';
import { ensureCanAdd, hasAddPermission } from 'utils/permissions';

interface RoleLookupProps {
  name: string;
  value?: any;
  onChange?: (value: any) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  loading?: boolean;
  onClear?: () => void;
  showClearButton?: boolean;
  showAddButton?: boolean;
  onRoleCreated?: (newRole: Role) => void;
  multiple?: boolean;
  labelAbove?: boolean;
}

const RoleLookup = ({
  name,
  value,
  onChange,
  label = 'Role',
  placeholder = 'Select a role',
  disabled = false,
  required = false,
  error = false,
  helperText,
  size = 'medium',
  fullWidth = true,
  loading = false,
  onClear,
  showClearButton = true,
  showAddButton = true,
  onRoleCreated,
  multiple = false,
  labelAbove = true
}: RoleLookupProps) => {
  const [openModal, setOpenModal] = useState(false);
  const { data: roles, isLoading, mutate: mutateRoleLookupList } = useGetRoleLookupList();
  const { permissions } = useGetPermissions();
  const navigate = useNavigate();
  const featureId = 2; // Roles
  const allowAdd = hasAddPermission(featureId, permissions);
  // const formik = useFormikContext<any>();
  const formik = React.useContext(FormikContext);

  const selectedRole = React.useMemo(() => {
    if (!roles?.length) return multiple ? [] : null;

    if (multiple) {
      if (Array.isArray(value) && value.length > 0) {
        return roles.filter((c: Role) => value.includes(c.userRoleID));
      }
      return [];
    } else {
      if (value) {
        return roles.find((c: Role) => c.userRoleID === value) || null;
      }
      return null;
    }
  }, [value, roles, multiple]);

  const handleAddRole = async () => {
    const ok = await ensureCanAdd(featureId, (path, opts) => navigate(path, opts));
    if (!ok) return;
    setOpenModal(true);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    const emptyValue = multiple ? [] : null;
    if (formik) {
      formik.setFieldValue(name, emptyValue, true);
      formik.setFieldTouched(name, true, true);
    }
    onChange?.(emptyValue);
    onClear?.();
  };

  const handleRoleCreated = async (newRole: Role | Role[]) => {
    const role = Array.isArray(newRole) ? newRole[0] : newRole;
    const normalizedNewRole: Role = {
      ...role,
      userRoleID: (role as any).userRoleID ?? (role as any).primaryKeyID
    } as Role;

    if (multiple) {
      const newSelection = [...(selectedRole || []), normalizedNewRole];
      const ids = newSelection.map((r: Role) => r.userRoleID);
      if (formik) {
        formik.setFieldValue(name, ids, true);
        formik.setFieldTouched(name, true, true);
      }
      onChange?.(ids);
    } else {
      const id = normalizedNewRole.userRoleID;
      if (formik) {
        formik.setFieldValue(name, id, true);
        formik.setFieldTouched(name, true, true);
      }
      onChange?.(id);
    }

    onRoleCreated?.(normalizedNewRole);
    mutateRoleLookupList();
  };

  const handleOnChange = (newValue: any) => {
    if (multiple) {
      const ids = newValue ? newValue.map((r: Role) => r.userRoleID) : [];
      if (formik) {
        formik.setFieldValue(name, ids, true);
        if (formik.touched[name]) {
          formik.setFieldTouched(name, true, true);
        }
      }
      onChange?.(ids);
    } else {
      const id = newValue ? newValue.userRoleID : null;
      if (formik) {
        formik.setFieldValue(name, id, true);
        if (formik.touched[name]) {
          formik.setFieldTouched(name, true, true);
        }
      }
      onChange?.(id);
    }
  };

  const handleGetOptionLabel = (option: any) => {
    return option?.roleName || '';
  };

  return (
    <>
      <Lookup
        lookupID="role-name"
        label={label}
        placeholder={placeholder}
        size={size}
        fullWidth={fullWidth}
        showClearButton={showClearButton}
        showAddButton={showAddButton}
        loading={isLoading}
        disabled={disabled}
        required={required}
        error={error}
        helperText={helperText}
        allowAdd={allowAdd}
        value={!multiple ? selectedRole : undefined}
        selectedOptions={multiple ? selectedRole : undefined}
        dataSource={roles}
        addButtonTitle="Add New Role"
        getOptionLabel={handleGetOptionLabel}
        isOptionEqualToValue={(option, value) => option.userRoleID === value?.userRoleID}
        onChange={handleOnChange}
        onBlur={() => (formik ? formik.setFieldTouched(name, true, true) : undefined)}
        handleAddNewRecord={handleAddRole}
        handleClear={handleClear}
        multiple={multiple}
        labelAbove={labelAbove}
      />

      {openModal && <RoleModal open={openModal} modalToggler={() => setOpenModal(false)} role={null} onRoleCreated={handleRoleCreated} />}
    </>
  );
};

export default RoleLookup;
